import type { Lens } from '@hookform/lenses';
import { z } from 'zod/v4';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { Typography } from '@interxfi/design-system/components/typography';
import {
  IxcRegistrationV1CipOfac,
  IxcRegistrationV1CipVendor,
  IxmUserauthV1BankAccountValidation,
  type IxmUserauthV1Vendors,
} from '@interxfi/types/generated/api';
import { AppEnumSelectInputWithLens } from '../../../../components/form/app-enum-select-input';
import { addDefinedValues } from '../../../../util/has-value';

export const programVendorsFormSchema = z.object({
  cipIdentity: z.enum(IxcRegistrationV1CipVendor).or(z.literal('')),
  cipOfac: z.enum(IxcRegistrationV1CipOfac).or(z.literal('')),
  bankAccountValidation: z.enum(IxmUserauthV1BankAccountValidation).or(z.literal('')),
});

type ProgramVendorsFormValues = z.infer<typeof programVendorsFormSchema>;

export const defaultProgramVendorsFormValues: ProgramVendorsFormValues = {
  cipIdentity: '',
  cipOfac: '',
  bankAccountValidation: '',
};

export function getInitialFormValuesForProgramVendors(data: IxmUserauthV1Vendors | undefined): ProgramVendorsFormValues {
  if (!data) {
    return defaultProgramVendorsFormValues;
  }

  return {
    cipIdentity: data.cipIdentity || '',
    cipOfac: data.cipOfac || '',
    bankAccountValidation: data.bankAccountValidation || '',
  };
}

export function formValuesToProgramVendors(values: ProgramVendorsFormValues): IxmUserauthV1Vendors {
  return addDefinedValues<IxmUserauthV1Vendors>(
    {},
    {
      cipIdentity: values.cipIdentity || undefined,
      cipOfac: values.cipOfac || undefined,
      bankAccountValidation: values.bankAccountValidation || undefined,
    },
  );
}

interface ProgramVendorsFormProps {
  lens: Lens<ProgramVendorsFormValues>;
}

export function ProgramVendorsForm({ lens }: ProgramVendorsFormProps) {
  return (
    <VerticalStack gap={4}>
      <Typography.Level3>Correspondent Vendors</Typography.Level3>

      <AppEnumSelectInputWithLens
        enumName="IxcRegistrationV1CipVendor"
        enumValue={IxcRegistrationV1CipVendor}
        i18nNamespace="ixcRegistrationV1"
        label="CIP Identity"
        lens={lens.focus('cipIdentity')}
      />

      <AppEnumSelectInputWithLens
        enumName="IxcRegistrationV1CipOfac"
        enumValue={IxcRegistrationV1CipOfac}
        i18nNamespace="ixcRegistrationV1"
        label="CIP OFAC"
        lens={lens.focus('cipOfac')}
      />

      <AppEnumSelectInputWithLens
        enumName="IxmUserauthV1BankAccountValidation"
        enumValue={IxmUserauthV1BankAccountValidation}
        i18nNamespace="ixmUserauthV1"
        label="Bank Account Validation"
        lens={lens.focus('bankAccountValidation')}
      />
    </VerticalStack>
  );
}
